import axios from 'axios'
import qs from 'querystring'
import { isExpired } from 'lib/token-utils'
import { getWafToken, isWafEnabled } from 'lib/aws-waf'
import { requestCaptchaSolve } from 'lib/waf-bridge'
import { getLocalStorage } from '../get-local-storage'

const IS_DEV = process.env.NODE_ENV === 'development'
const IS_SERVER = typeof window === 'undefined'
const extraHeaders = {}
//
// MILO:
//       In the interest of my RAM I run without Kong
//       in dev, so we can spoof our credentials...
//       Note also the API won't mint a token without
//       verifying the Kong consumer exists so you have
//       to set one manually in localStorage (at 'apiToken')
//
if (IS_DEV) {
  extraHeaders['x-consumer-id'] = 'FAKE BOY'
  extraHeaders['x-consumer-name'] = 'scitedevapi'
} else if (IS_SERVER) {
  //
  // Server-side (SSR) calls reach FastAPI directly through the internal load
  // balancer, bypassing Kong; set the consumer headers Kong would normally add.
  //
  extraHeaders['x-consumer-id'] = CONFIG.apiBackendConsumer.id
  extraHeaders['x-consumer-name'] = CONFIG.apiBackendConsumer.name
}

const localStorage = getLocalStorage()

export const getUrl = () => {
  if (IS_DEV && !IS_SERVER) {
    return `http://${window.location.hostname}:8000`
  }

  //
  // Server-side (SSR) the Node backend reaches FastAPI directly through the
  // internal load balancer, bypassing CloudFront/Kong. The browser keeps using
  // the public apiURL.
  //
  if (IS_SERVER) {
    return CONFIG.apiInternalURL || CONFIG.apiURL
  }

  return CONFIG.apiURL
}

export const api = axios.create({
  baseURL: getUrl(),
  paramsSerializer: params => qs.stringify(params),
  headers: {
    Authorization: {
      toString() {
        //
        // MILO: This is kind of a hack and I apologize
        //       but seems less likely to accidentally leak the secret
        //       into the client bundle than using eg config.get()
        //
        if (IS_SERVER) {
          return `Bearer ${global.SCITE_API_JWT_TOKEN_BACKEND}`
        }
        return `Bearer ${localStorage?.getItem('apiToken')}`
      }
    },
    ...extraHeaders
  }
})

export const webApi = axios.create({
  baseURL: IS_SERVER ? 'http://localhost:3000/api' : '/api',
  headers: {
    Authorization: {
      toString() {
        if (IS_SERVER) {
          return undefined
        }
        return `Bearer ${localStorage?.getItem('apiToken')}`
      }
    },
    ...extraHeaders
  }
})

let tokenRefreshing = null

//
// Check before we send a request if we need to refresh the
// user's API token + refresh it if we do.
//
api.interceptors.request.use(
  async config => {
    if (IS_SERVER) {
      return config
    }

    const apiToken = localStorage?.getItem('apiToken')

    if (!apiToken) {
      return config
    }

    if (isExpired(apiToken)) {
      if (!tokenRefreshing) {
        tokenRefreshing = webApi.get('/auth/api_token')
      }

      try {
        const { data: newToken } = await tokenRefreshing
        localStorage?.setItem('apiToken', newToken)
        tokenRefreshing = null
      } catch (e) {
        localStorage?.removeItem('apiToken')
        tokenRefreshing = null
        throw e
      }
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

//
// Attach the AWS WAF token to every browser request so AWS WAF Bot Control can
// evaluate it at the edge. No-ops server-side: SSR traffic reaches FastAPI
// through the internal load balancer, behind the WAF entirely.
//
// A missing token is deliberately not fatal: the WAF starts out in COUNT mode,
// so the request goes out bare and the edge decides. getWafToken has already
// surfaced a one-time toast in that case. Revisit if we ever move to BLOCK.
//
const attachWafToken = async config => {
  // isWafEnabled() is false server-side, so this covers SSR too.
  if (!isWafEnabled()) {
    return config
  }

  const token = await getWafToken()
  if (token) {
    config.headers['x-aws-waf-token'] = token
  }

  return config
}

api.interceptors.request.use(attachWafToken)
webApi.interceptors.request.use(attachWafToken)

//
// AWS WAF answers a blocked request differently depending on the rule action:
// CAPTCHA sends HTTP 405 and needs the interactive puzzle solved before a retry
// can succeed. Challenge sends HTTP 202 and is solved silently - the retried
// request re-enters the request interceptor chain below, so attachWafToken
// fetches a fresh token on its own; no separate solve step needed here. Both
// need the WAF custom response to send CORS headers for cross-origin (api
// host) calls, otherwise the browser cannot read the response and neither
// branch fires.
//
// `onGiveUp` supplies the original outcome when the user dismisses the CAPTCHA
// puzzle. It's passed as `then`'s second argument rather than a trailing
// `.catch` so it only covers requestCaptchaSolve - a genuine failure from the
// retried request still propagates instead of being masked by the original 405.
//
// A retried request can still come back challenged (the token didn't land in
// time, or the WAF SDK never loaded). The retry-once guard below must not let
// that second 405/202 fall through as if it were a real response: both have an
// empty body, and a caller that destructures real fields out of it crashes
// instead of failing cleanly.
const isWafChallengeStatus = status => status === 405 || status === 202

const retryAfterWafChallenge = (client, response, config, onGiveUp) => {
  if (!response || !config) {
    return null
  }

  if (config.__wafChallengeRetried) {
    if (isWafChallengeStatus(response.status)) {
      return Promise.reject(Object.assign(new Error('AWS WAF challenge unresolved after retry'), { response, config }))
    }
    return null
  }

  if (response.status === 405) {
    config.__wafChallengeRetried = true
    return requestCaptchaSolve().then(() => client(config), onGiveUp)
  }

  if (response.status === 202) {
    config.__wafChallengeRetried = true
    return client(config)
  }

  return null
}

//
// Registered on both the fulfilled and rejected handlers on purpose. Callers
// that set `validateStatus` — lib/api/client.ts does, so openapi-fetch can map
// statuses itself — make axios *resolve* on a 405/202, so a rejected-only
// handler would silently skip the challenge for them.
//
// Two ways this can still end unresolved, handled differently:
// - The puzzle is dismissed: gives up back into the caller's own contract - the
//   fulfilled side resolves with the original response (a caller that opted out
//   of throwing must not start throwing), the rejected side re-rejects with the
//   original error (the caller cares that its request failed, not that a puzzle
//   was dismissed).
// - The retry itself comes back challenged again: both sides reject with a
//   synthetic error instead, since an empty challenge response has no real data
//   for either kind of caller to fall back on.
//
const installWafChallengeRetry = client => {
  client.interceptors.response.use(
    response => retryAfterWafChallenge(client, response, response.config, () => response) || response,
    error => {
      const giveUp = () => Promise.reject(error)
      return retryAfterWafChallenge(client, error.response, error.config, giveUp) || giveUp()
    }
  )
}

installWafChallengeRetry(api)
installWafChallengeRetry(webApi)

export default api
